import React, { useEffect, useState } from "react";
import {connect} from "react-redux";
import {Link} from "react-router-dom";
import styles from "./ProviderReports.scss";
import InformationTile from "components/common/InformationTile";
import {PermissionTypes, userHasPermission} from "utils/permissions/rolesPermissions";
import * as StyleId from "constants/CssStyleIds";
import * as UserPermissions from "constants/UserPermissions";
import { getProviderBasePath } from "utils/routingUtils";
import * as ProviderApi from 'api/ProviderApi';
import PropTypes from "prop-types";
import Modal from "components/common/Modal";
import * as ModalTypes from "constants/ModalTypes";
import SpinnerTakeover from "components/common/SpinnerTakeover";

function CorporateGroupReports(props) {
    const [providerReports, setProviderReports] = useState([]);
    const [dashboard, setDashboard] = useState(null);
    const basePath = getProviderBasePath(props.path, props.providerId, props.nodeId);

        
    useEffect(() => {
        ProviderApi.getProviderReports(props.providerId)
            .then((res) => {
                setProviderReports(res.body.dashboards);
            })
            .catch((error) => {
                handleErrorResponse("getting provider reports", error);
            });
    }, [dashboard]);

    const handleTileClick = (dashboardId) => {
        setDashboard(providerReports.find(report => report.dashboardId === dashboardId))
    }

    const renderReportTiles = () => {
        const tiles = providerReports.map(dashboard => {
            return (
                <div
                    key={`dashboard_${dashboard.dashboardId}`}
                    onClick={() => handleTileClick(dashboard.dashboardId)}
                    style={{cursor: "pointer"}}
                >
                        <InformationTile
                            title={dashboard.reportName}
                        >
                        <div className={styles.tileInfo}>
                            <div className={styles.tileIcon}>
                                <i className={dashboard.iconUrl}/>
                            </div>
                            <div className={styles.description}>
                                {dashboard.description}
                            </div>
                        </div>
                        </InformationTile>
                </div>
            );
        });

        if(props.canViewCouponAudit && props.clinicId) {
            tiles.unshift(
                <div key={"cumulativeOffers"}>
                    <Link to={`${basePath}/reports/cumulative-offer-tracking`}>
                        <InformationTile
                            title={"Cumulative Offer Tracking"}
                        >
                            <div className={styles.tileInfo}>
                                <div className={styles.tileIcon}>
                                    <i className={"fas fa-chart-bar"}/>
                                </div>
                                <div className={styles.description}>
                                    View summary of parasiticide sales in comparison to previous year
                                </div>
                            </div>
                        </InformationTile>
                    </Link>
                </div>
            )
        }

        if(props.canViewClientCampaignTracking) {
            tiles.unshift(
                <div key="clientCampaignTracking">
                    <Link to={`${basePath}/reports/client-campaign-tracking`}>
                        <InformationTile
                            title="Pet Owner List"
                        >
                            <div className={styles.tileInfo}>
                                <div className={styles.tileIcon}>
                                    <i className={"fas fa-mail-bulk"}/>
                                </div>
                                <div className={styles.description}>
                                    View mailing lists to manually opt-out specific clients from Merck Email Campaigns.
                                </div>
                            </div>
                        </InformationTile>
                    </Link>
                </div>
            );
        }

        if(props.canViewProviderPayoutReport) {
            tiles.unshift(
                <div key="providerCheckSummary">
                    <Link to={`${basePath}/reports/payout-report`}>
                        <InformationTile
                            title="Payout Report"
                        >
                            <div className={styles.tileInfo}>
                                <div className={styles.tileIcon}>
                                    <i className={"fa-solid fa-money-check-pen"}/>
                                </div>
                                <div className={styles.description}>
                                    Manufacturer Check Totals By Clinic.
                                </div>
                            </div>
                        </InformationTile>
                    </Link>
                </div>
            );
        }

        tiles.unshift(
            <div key="marketShare">
                <InformationTile
                    title="Custom Reports"
                    style={StyleId.INACTIVE}
                    overlay="Coming Soon"
                >
                    <div className={styles.tileInfo}>
                        <div className={styles.tileIcon}>
                            <i className={"fas fa-chart-pie"} />
                        </div>
                        <div className={styles.description}>
                            Soon you'll be able to view reports here
                        </div>
                    </div>
                </InformationTile>
            </div>
        );

        if(props.canViewCouponAudit) {
            tiles.unshift(
                <div key={"corporateGroupAuditHistory"}>
                    <Link to={`${basePath}/reports/corp-audit-history`}>
                        <InformationTile
                            title={"Corporate Audit History"}
                        >
                            <div className={styles.tileInfo}>
                                <div className={styles.tileIcon}>
                                    <i className={"fas fa-badge-dollar"}/>
                                </div>
                                <div className={styles.description}>
                                    View coupon audit history for a 30 day period
                                </div>
                            </div>
                        </InformationTile>
                    </Link>
                </div>
            )
        }

        return tiles;
    }

    return (
        <div className={styles.root}>
            <div className={styles.gridContainer}>
                <div className={styles.grid}>
                    {renderReportTiles()}
                </div>
            </div>
            <Modal
                show={!!dashboard}
                onClose={() => setDashboard(null)}
                modalTitle={`${dashboard?.reportName || ''}`}
                modalType={ModalTypes.FULLSCREEN}
                flexBody
            >
                <iframe
                    src={dashboard?.dashboardUrl || ''}
                    className={styles.manufacturerReport}
                />
            </Modal>
            <SpinnerTakeover show={!providerReports} />
        </div>
    );
}

CorporateGroupReports.propTypes = {
    path: PropTypes.string.isRequired,
    providerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    nodeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
}
export default connect(
    (state, ownProps) => {
        const params = ownProps?.match?.params || {};
        const userProfile = state.user.userProfile;
        const node = state.entities.nodes?.[ownProps.nodeId];
        //Permissions
        const canViewCorporateGroupClinic = userHasPermission(PermissionTypes.VIEW, UserPermissions.CORPORATE_GROUP_CLINIC, userProfile);
        const canViewProviderPayoutReport = userHasPermission(PermissionTypes.VIEW, UserPermissions.PROVIDER_PAYOUT_REPORT, userProfile);
        const canViewCouponAudit = userHasPermission(PermissionTypes.VIEW, UserPermissions.CLINIC_COUPON_AUDIT, userProfile);

        return {
            userProfile,
            node,
            isAdmin: !!(ownProps?.location?.pathname?.includes("/admin") && ownProps.providerId && ownProps.nodeId),
            territoryId: params.territoryId,
            districtId: params.districtId,
            regionId: params.regionId,

            // Permissions
            canViewCorporateGroupClinic,
            canViewProviderPayoutReport,
            canViewCouponAudit,
        };
    }
)(CorporateGroupReports);
